import { Injectable } from '@angular/core';

export interface PhotoResult {
  dataUrl: string;
  format: string;
  saved: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CameraService {
  private isNativePlatform: boolean = false;

  constructor() {
    this.checkPlatform();
  }

  private async checkPlatform() {
    try {
      const { Capacitor } = await import('@capacitor/core');
      this.isNativePlatform = Capacitor.isNativePlatform();
    } catch {
      this.isNativePlatform = false;
    }
  }

  async checkCameraPermission(): Promise<boolean> {
    try {
      const { Camera } = await import('@capacitor/camera');
      const permissions = await Camera.checkPermissions();
      return permissions.camera === 'granted';
    } catch (error) {
      return false;
    }
  }

  async requestCameraPermission(): Promise<boolean> {
    try {
      const { Camera } = await import('@capacitor/camera');
      const permissions = await Camera.requestPermissions({ permissions: ['camera'] });
      return permissions.camera === 'granted';
    } catch (error) {
      return false;
    }
  }

  async checkPermissions(): Promise<boolean> {
    return this.checkCameraPermission();
  }

  async requestPermissions(): Promise<boolean> {
    return this.requestCameraPermission();
  }

  async takePhoto(): Promise<PhotoResult | null> {
    try {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      const hasPermission = await this.checkCameraPermission();
      if (!hasPermission) {
        const granted = await this.requestCameraPermission();
        if (!granted) {
          throw new Error('Camera permissions not granted');
        }
      }

      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        quality: 90,
        allowEditing: true,
        width: 1024,
        height: 1024,
        correctOrientation: true,
        saveToGallery: false
      });

      if (!photo.dataUrl) {
        throw new Error('No image data received');
      }

      return {
        dataUrl: photo.dataUrl,
        format: photo.format || 'jpeg',
        saved: false
      };
    } catch (error) {
      return null;
    }
  }

  async pickFromGallery(): Promise<PhotoResult | null> {
    try {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');

      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
        quality: 90,
        allowEditing: true,
        width: 1024,
        height: 1024,
        correctOrientation: true
      });

      if (!photo.dataUrl) {
        throw new Error('No image data received');
      }

      return {
        dataUrl: photo.dataUrl,
        format: photo.format || 'jpeg',
        saved: false
      };
    } catch (error) {
      return null;
    }
  }

  async pickPhoto(): Promise<PhotoResult | null> {
    try {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      const hasPermission = await this.checkCameraPermission();
      if (!hasPermission) {
        const granted = await this.requestCameraPermission();
        if (!granted) {
          throw new Error('Camera permission not granted');
        }
      }

      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
        quality: 90,
        allowEditing: true,
        width: 1024,
        height: 1024,
        correctOrientation: true,
        promptLabelHeader: 'Select Photo',
        promptLabelPhoto: 'From Gallery',
        promptLabelPicture: 'Take Photo'
      });

      if (!photo.dataUrl) {
        throw new Error('No image data received');
      }

      return {
        dataUrl: photo.dataUrl,
        format: photo.format || 'jpeg',
        saved: false
      };
    } catch (error) {
      return null;
    }
  }

  dataUrlToBlob(dataUrl: string): Blob | null {
    try {
      const arr = dataUrl.split(',');
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch) return null;
      
      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      
      return new Blob([u8arr], { type: mime });
    } catch (error) {
      return null;
    }
  }

  async resizeImage(dataUrl: string, maxWidth: number, maxHeight: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = height * (maxWidth / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = width * (maxHeight / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = dataUrl;
    });
  }
}
